import { RichTextBlockType_richTextBlock } from "@components/RichTextBlock/queries/contentfulTypes/RichTextBlockType";
import { BlockSize, Sizes, getContextSize } from "@lib/contentArea";
import { getHeadingVariants } from "@utils/headingHelper";
import { CheckRichTextNotEmpty } from "@utils/string";
import clsx from "clsx";
import React from "react";

import { ContentAreaItem } from "types/ContentAreaItem";
import { Context } from "types/context/Context";

import Render from "./Render";
import contentAreaStyles from "./contentArea.module.css";

// Re-exported so existing importers keep working
export type { Sizes };
export { BlockSize };

interface IContentfulContentArea {
  contentAreaItems?: ReadonlyArray<unknown>;
  context: Context;
  maxWidth?: number;
  mobileColumns?: number;
  tabletColumns?: number;
  desktopColumns: number;
  notebookColumns?: number;
  heading?: string;
}

const CFContentArea = ({
  contentAreaItems: rawItems = [],
  context,
  mobileColumns = 1,
  tabletColumns = 1,
  notebookColumns = 1,
  desktopColumns,
  maxWidth,
  heading = "",
}: IContentfulContentArea): React.ReactNode => {
  const contentAreaItems = rawItems as ContentAreaItem[];
  const { accordion, sectionBlock, relatedContent, faqPageContent } = context;

  let shouldRenderContent = true;
  if (
    contentAreaItems.length === 1 &&
    Object.keys(contentAreaItems[0])[0]?.toLowerCase() === "richtextblock"
  ) {
    const blockData = contentAreaItems[0][
      Object.keys(contentAreaItems[0])[0]
    ] as unknown as RichTextBlockType_richTextBlock;
    shouldRenderContent = CheckRichTextNotEmpty(blockData);
  }

  const baseContext: Context = { ...context, heading: getHeadingVariants(heading) };
  const columns = {
    mobile: mobileColumns,
    tablet: tabletColumns,
    notebook: notebookColumns,
    desktop: desktopColumns,
  };

  const classes = clsx(
    contentAreaStyles.contentArea,
    contentAreaStyles[`mobileCols${mobileColumns}`],
    contentAreaStyles[`tabletCols${tabletColumns}`],
    contentAreaStyles[`notebookCols${notebookColumns}`],
    contentAreaStyles[`desktopCols${desktopColumns}`],
    accordion && contentAreaStyles.accordion,
    sectionBlock && contentAreaStyles.sectionBlock,
    relatedContent && contentAreaStyles.relatedContent,
    faqPageContent && contentAreaStyles.faqPageContent,
  );

  let columnPosition = 0;
  let sectionBlockIndex = 0;

  return (
    <>
      {shouldRenderContent && (
        <div className={classes}>
          {contentAreaItems.map((contentAreaItem, i) => {
            columnPosition = i % desktopColumns === 0 ? 1 : columnPosition + 1;

            const blockName = Object.keys(contentAreaItem)[0];
            if (blockName.toLowerCase() === "sectionblock") {
              sectionBlockIndex += 1;
            }

            const key = contentAreaItem[blockName]?.id ?? `${blockName}-${i}`;
            const sizes = getContextSize(i, columnPosition, contentAreaItems.length, columns);
            const itemContext: Context = { ...baseContext, sizes, sectionBlockIndex };

            return (
              <Render
                key={`${key}-${i}`}
                uniqueKey={`${key}-${i}`}
                contentAreaItem={contentAreaItem}
                context={itemContext}
                maxWidth={maxWidth}
              />
            );
          })}
        </div>
      )}
    </>
  );
};

export default CFContentArea;
